import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { X, Printer, Download, Loader2 } from 'lucide-react'
import { downloadTextFile } from '../../../lib/download'

// In-app, full-screen preview of the generated period report. The
// HTML doc is rendered inside an <iframe srcDoc> so the report's
// own <style> tag doesn't leak into the app shell. Mobile-friendly:
// gives the user a usable preview without forcing them to figure
// out how to open a downloaded .html file (iOS especially is hostile
// to HTML downloads).
//
// Action bar at the top: close, print (which on mobile shows the
// system share-sheet with "Save to PDF"), and download.

export default function ReportPreviewModal({ html, filename, onClose }) {
  const { t } = useTranslation()
  const iframeRef = useRef(null)
  const [loaded, setLoaded] = useState(false)

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow
    if (!win) return
    win.focus()
    try {
      win.print()
    } catch {
      // Some browsers (older WebKit) reject programmatic print on
      // cross-origin or sandboxed iframes — open the doc in a new
      // tab as a fallback so the user can use the browser's own
      // Print menu.
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 5000)
    }
  }

  const handleDownload = () => {
    downloadTextFile(filename, html, 'text/html;charset=utf-8')
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] bg-black/80 flex flex-col"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 bg-background border-b border-input flex-shrink-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.5rem)' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="p-2 -ms-1 rounded-md hover:bg-accent text-foreground"
          aria-label={t('common.close')}
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="flex-1 text-sm font-semibold text-foreground truncate" dir="auto">
          {t('reports.preview')}
        </h2>
        <button
          type="button"
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-app-primary-500 text-white text-sm font-medium hover:bg-app-primary-500/90"
        >
          <Printer className="w-4 h-4" />
          <span className="hidden sm:inline">{t('reports.print')}</span>
        </button>
        <button
          type="button"
          onClick={handleDownload}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-input bg-background text-foreground text-sm hover:bg-accent"
        >
          <Download className="w-4 h-4" />
          <span className="hidden sm:inline">{t('reports.download')}</span>
        </button>
      </div>
      <div className="flex-1 bg-white relative overflow-hidden">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}
        <iframe
          ref={iframeRef}
          title="report-preview"
          srcDoc={html}
          onLoad={() => setLoaded(true)}
          className="w-full h-full border-0 bg-white"
        />
      </div>
    </div>,
    document.body,
  )
}
